import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import api from 'api';
import ResolvePagination from 'components/ResolvePagination';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { DataRoomInvitation } from 'router/subrouters/DataRoom/models/DataRoom';
import DataRoomInvitationCard from './components/DataRoomInvitationCard';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { BODY_CONTENT_GENERATE } from './components/DataRoomInvitationsTableCells';
import {
  SORTING_VALUES,
  HEADING_TRANSLATIONS,
  TABLE_COLUMNS,
} from './components/DataRoomInvitationsTableCells/data';
import { CUSTOM_COL_SIZES, DR_INVITATION_PAGE } from '../TableCells/data';
import { getDataRoomRoute } from 'utils';
import { useNavigate } from 'react-router-dom';
import PortfolioPlaceholderCards from '../../../../components/PortfolioPlaceholderCards';
import { SIZE } from 'models/Size';
import usePageParams from 'hooks/usePageParams';
import useScrollIntoView from 'hooks/useScrollIntoView';

import './DataRoomInvitations.styles.scss';

type DataRoomInvitationsProps = {
  className?: string;
  dataLimit: string;
  isTableView?: boolean;
  hideColumns?: string[];
  isEmpty: boolean;
  setIsEmptyDRInvitation: (value: boolean) => void;
};

const DataRoomInvitations: React.FC<DataRoomInvitationsProps> = (props) => {
  const {
    className,
    dataLimit,
    isTableView,
    hideColumns,
    isEmpty,
    setIsEmptyDRInvitation,
  } = props;

  const classes = classNames('data-room-invitations', className);

  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [orderedColumns, setOrderedColumns] = useState(TABLE_COLUMNS);
  const [dataRoomInvitations, setDataRoomInvitations] = useState<
    DataRoomInvitation[]
  >([]);

  const {
    params: { drInvitationSort, timeframe },
    setQueryParam,
  } = useQueryParams();

  const [contentRef, scrollToStart] = useScrollIntoView();

  const drInvitationPage = usePageParams(DR_INVITATION_PAGE);

  const tableColumns = useMemo(
    () => orderedColumns.filter((el) => !hideColumns.includes(el)),
    [hideColumns, orderedColumns],
  );

  const renderDeals = useMemo(
    () =>
      isLoading ? (
        <PortfolioPlaceholderCards
          numberOfCards={+dataLimit}
          size={SIZE.SMALL}
        />
      ) : (
        dataRoomInvitations.map(
          ({
            listing,
            dealRoom: {
              _id,
              portfolioName,
              industry,
              dealOutcome,
              otherDealOutcome,
              dataRoomId,
            },
          }) => (
            <DataRoomInvitationCard
              key={_id}
              title={portfolioName}
              industry={industry}
              dealOutcome={dealOutcome}
              otherDealOutcome={otherDealOutcome}
              dataRoomId={dataRoomId}
              imageSrc={listing?.gallery?.[0]?.url}
            />
          ),
        )
      ),
    [dataLimit, dataRoomInvitations, isLoading],
  );

  const tableContent = useMemo(
    () =>
      orderedColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: ({ dealRoom, listing, createdAt }: DataRoomInvitation) =>
            BODY_CONTENT_GENERATE[col]({
              dealRoom,
              listing,
              createdAt,
            }),
        }),
        {},
      ),
    [orderedColumns],
  );

  const navigate = useNavigate();

  const { t } = useTranslation();

  const onReorderL = useCallback((c) => setOrderedColumns(c), []);

  useEffect(() => {
    (async () => {
      if (!drInvitationSort) return;

      try {
        setIsLoading(true);
        const {
          data: { totalPages, items },
        } = await api.portfolio.buyer.getDataRoomInvitations({
          $relations: '["listing"]',
          $page: drInvitationPage,
          $sort: drInvitationSort,
          $limit: dataLimit,
          timeframe,
        });

        setTotalPages(totalPages);
        setDataRoomInvitations(items);
        setIsEmptyDRInvitation(!items.length);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    drInvitationPage,
    drInvitationSort,
    timeframe,
    dataLimit,
    setIsEmptyDRInvitation,
    setIsLoading,
  ]);

  if (isEmpty) return null;

  return (
    <div className={classes} ref={contentRef}>
      <p className="title">{t('dataRoomInvitations')}</p>
      <div className="resolve-page-items">
        {!isTableView ? (
          <>
            {renderDeals}
            <ResolvePagination
              pageParam={DR_INVITATION_PAGE}
              totalPages={totalPages}
              onClick={scrollToStart}
            />
          </>
        ) : (
          <ReorderResizeTable
            className="portfolio-results-table"
            headingTranslations={HEADING_TRANSLATIONS}
            tableContentTransformations={tableContent}
            columns={tableColumns as Array<keyof DataRoomInvitation>}
            sortingValues={SORTING_VALUES}
            isDataLoading={isLoading}
            data={dataRoomInvitations}
            customSizes={CUSTOM_COL_SIZES}
            sort={drInvitationSort}
            totalPages={totalPages}
            pageParam={DR_INVITATION_PAGE}
            updateSort={(value) => setQueryParam('drInvitationSort', value)}
            onRowClick={({ dataRoomId }) =>
              navigate(getDataRoomRoute(dataRoomId))
            }
            onReorder={onReorderL}
            onPaginationClick={scrollToStart}
          />
        )}
      </div>
    </div>
  );
};

export default DataRoomInvitations;
